// ============================================================
// MANIC MEETER — API Client
// ============================================================

import { C, getLocale } from "./i18n";

const API_BASE = "";  // same-origin, no prefix needed

interface ApiResponse<T = unknown> {
  ok: boolean;
  data?: T;
  error?: string;
}

function getToken(): string | null {
  return localStorage.getItem("manic-meeter-token");
}

function setToken(token: string): void {
  localStorage.setItem("manic-meeter-token", token);
}

function clearToken(): void {
  localStorage.removeItem("manic-meeter-token");
  localStorage.removeItem("manic-meeter-guest-token");
}

function getGuestToken(): string | null {
  return localStorage.getItem("manic-meeter-guest-token");
}

function setGuestToken(token: string): void {
  localStorage.setItem("manic-meeter-guest-token", token);
}

async function apiRequest<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
  // The server answers in whichever language the player is playing in.
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Accept-Language": getLocale(),
  };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.error || C().ui.api.serverError };
    return { ok: true, data };
  } catch {
    return { ok: false, error: C().ui.api.connectionFailed };
  }
}

// ---- Auth ----

export async function loginAsGuest(): Promise<ApiResponse<{ token: string; userId: number; guestToken: string }>> {
  const res = await apiRequest<{ token: string; userId: number; guestToken: string }>("POST", "/api/auth/guest");
  if (res.ok && res.data) {
    setToken(res.data.token);
    setGuestToken(res.data.guestToken);
  }
  return res;
}

export async function register(username: string, email: string, password: string): Promise<ApiResponse<{ token: string; userId: number; upgraded?: boolean }>> {
  const guestToken = getGuestToken();
  const res = await apiRequest<{ token: string; userId: number; upgraded?: boolean }>("POST", "/api/auth/register", {
    username, email, password, guestToken,
  });
  if (res.ok && res.data) {
    setToken(res.data.token);
    localStorage.removeItem("manic-meeter-guest-token");
  }
  return res;
}

export async function login(loginStr: string, password: string): Promise<ApiResponse<{ token: string; userId: number }>> {
  const res = await apiRequest<{ token: string; userId: number }>("POST", "/api/auth/login", {
    login: loginStr, password,
  });
  if (res.ok && res.data) {
    setToken(res.data.token);
    localStorage.removeItem("manic-meeter-guest-token");
  }
  return res;
}

export async function getMe(): Promise<ApiResponse<{ id: number; username: string; email: string; is_guest: boolean }>> {
  return apiRequest("GET", "/api/auth/me");
}

export function logout(): void {
  clearToken();
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

// ---- Profile ----

export interface ProfileData {
  player_name: string;
  player_role: string;
  salary: number;
  fatigue: number;
  reputation: number;
  meetings_survived: number;
  days_survived: number;
  achievements: string[];
  upgrades: Record<string, unknown>;
  stats: Record<string, unknown>;
  game_state: unknown;
}

export async function getProfile(): Promise<ApiResponse<ProfileData>> {
  return apiRequest("GET", "/api/profile");
}

export async function saveProfile(data: Partial<ProfileData>): Promise<ApiResponse<{ ok: boolean }>> {
  return apiRequest("PUT", "/api/profile", data);
}

// ---- Highscores ----

export interface HighscoreEntry {
  player_name: string;
  player_role: string;
  score: number;
  days_survived: number;
  meetings_survived: number;
  final_salary: number;
  longest_meeting: number;
  created_at: string;
}

export type HighscoreCategory = "score" | "days" | "salary" | "meetings";

export async function getHighscores(category: HighscoreCategory = "score"): Promise<ApiResponse<HighscoreEntry[]>> {
  return apiRequest("GET", `/api/highscores?category=${encodeURIComponent(category)}`);
}

export async function submitHighscore(data: {
  player_name: string;
  player_role: string;
  days_survived: number;
  meetings_survived: number;
  final_salary: number;
  longest_meeting?: number;
}): Promise<ApiResponse<{ ok: boolean; score: number }>> {
  return apiRequest("POST", "/api/highscores", data);
}

export async function getMyHighscores(): Promise<ApiResponse<HighscoreEntry[]>> {
  return apiRequest("GET", "/api/highscores/me");
}

// ---- Meta-Progression (Impostor Points, Upgrades, Powerups) ----

export async function saveMetaToServer(meta: unknown, role: string): Promise<ApiResponse<{ ok: boolean }>> {
  return apiRequest("PUT", "/api/profile", { game_state: meta, game_state_role: role });
}

export async function loadMetaFromServer(role: string): Promise<ApiResponse<ProfileData>> {
  return apiRequest("GET", `/api/profile?role=${encodeURIComponent(role)}`);
}
